import React from 'react';
import PropTypes from "prop-types";
import Modal from "react-modal";
import css from './CalendarDrawer.scss';
import Icon from '../SF/MaterialIcon';
import moment from 'moment';
import { getStaffMemberConnectionProfileUrl } from '../utils/successNetworkURL';
import { sortBy, isArray, isEmpty } from "lodash";
import ServicesBlock from './ServicesBlock';
import DrawerOpenButton from "./DrawerOpenButton";
import InterventionDetails from "./InterventionDetails";
import DrawerCard from "./DrawerCard";
import { RequestHelp } from "utils/types";

const CalendarDrawer = React.forwardRef(({isOpen, onClose, data, date, successToastMessage, requestHelp, isAllDayEvent}, ref) => {
  Modal.setAppElement(document.getElementById("main-center"));
  const allDayEvents = data.filter(({endTime}) => !endTime);

  // Sort all events which aren't all day by start time
  const singleTimeEvents = sortBy(
    data
      .filter(({endTime}) => endTime)
      .map(event => ({
        ...event, 
        startBlockTimestamp: parseInt(moment(`${event.date} ${event.startTime}`, "YYYY-MM-DD HH:mm").format("X")),
      })), 
    'startBlockTimestamp'
  );

  // Combine events in blocks. Events that start and finish at the same time are grouped
  // together. Events with different times are in their own
  const getCombinedEvents = () => {
    const result = [];
    singleTimeEvents.forEach((event, i) => {
      if ( i === 0 ) result.push([event]);
      else {
        const lastAdded = result[result.length - 1][0];
        if ( event.startTime === lastAdded.startTime && event.endTime === lastAdded.endTime ) {
          result[result.length - 1].push(event);
        } else {
          result.push([event])
        }
      }
    })
    return result.map(element => element.length === 1 ? element[0] : element);
  };

  const formatTimeHeader = event => {
    const startTime = moment(event.startTime, "HH:mm");
    const endTime = moment(event.endTime, "HH:mm");
    const crossTimes = startTime.format("a") !== endTime.format("a");
    return `${startTime.format(crossTimes ? "h:mm a" : "h:mm")}–${endTime.format("h:mm a")}`;
  }

  const getBlockIcon = event => {
    let [color, icon] = ["", ""];
    switch (event.type) {
      case "ASSIGNMENT":
        color = "#D20061";
        icon = 'assignment';
        break;
      case "APPOINTMENT":
        color = "#1E70A6";
        icon = 'event';
        break;
      case "GROUP_SESSION":
        color = "#1E70A6";
        icon = 'group';
        break;
      case "TO_DO":
        icon = 'check_box';
        color = "#B94713";
        break;
      case "FLAG":
        icon = 'flag';
        color = "#B94713";
        break;
      case "REFERRAL":
        icon = 'exit_to_app';
        color = "#B94713";
        break;
      case "CLASS":
        icon = 'menu_book';
        color = "#37A2A2";
        break;
    }
    return (
      <div 
        className={css['block-icon']} 
        style={{backgroundColor: color}}>
        <Icon aria-hidden={true} icon={icon} />
      </div>
    );
  }

  const formatCourseHours = schedule => {
    const weeklyDays = schedule.weeklyDays.split(", ").map(token => {
      switch (token) {
        case "S": return "Su";
        case "M": return "Mo";
        case "T": return "Tu";
        case "W": return "We";
        case "R": return "Th";
        case "F": return "Fr";
        case "A": return "Sa";
      }
    }).join(", ");
    return `${weeklyDays} at ${formatTimeHeader(schedule)}`
  };

  const getAssignmentDetails = event => (
    <div className={css['details']}>
      <div className={css['instructors']}>
        {event.details.instructors.map(instructor => (
          <div key={instructor.id} className={css['instructor']}>
            <Icon icon="person" /> <a href={getStaffMemberConnectionProfileUrl(instructor.id)} data-role='staffMemberProfileLink'>{instructor.name}</a>
          </div>
        ))}
        <div aria-label='Assignment Course' className={css['section']}>
          <img className={css['book-icon']} src="../images/starfish/icons/starfish_book.png" aria-hidden="true" /> 
          <span>{event.details.section.name} ({event.details.section.cmsCourseId})</span>
        </div>
      </div>
    </div>
  );

  const getCourseDetails = event => (
    <div className={css['course-details']}>
      <div className={css['instructors']}>
        {event.details.instructors.map(instructor => (
          <div key={instructor.id} className={css['instructor']}>
            <Icon icon="person" /> <a href={getStaffMemberConnectionProfileUrl(instructor.id)} data-role='staffMemberProfileLink'>{instructor.name}</a>
          </div>
        ))}
        {event.details.schedules.map(schedule => (
          <div key={schedule.id} className={css['instructor']}>
            <Icon icon="event" /> <span>{formatCourseHours(schedule)}</span>
          </div>
        ))}
      </div>
      {!isEmpty(event.details.services) && (
        <ServicesBlock 
          services={event.details.services} 
          sectionId={event.details.sectionId}   
        />
      )}
    </div>
  );

  const getAppointmentDetails = event => (
    <div className={css['details']}>
      <div className={css['instructors']}>
        <div className={css['instructor']}>
          <Icon icon="person" /> <a href={getStaffMemberConnectionProfileUrl(event.details.instructor.id)} data-role='staffMemberProfileLink'>{event.details.instructor.name}</a>
        </div>
        <div className={css['instructor']}>
          <Icon icon="room" /> <span>{event.details.location}</span>
        </div>
        {event.details.section && <div aria-label='Appointment Course' className={css['section']}>
          <img className={css['book-icon']} src="../images/starfish/icons/starfish_book.png" aria-hidden="true" /> 
          <span>{event.details.section.name} ({event.details.section.cmsCourseId})</span>
        </div>}
        <div className={css['instructor']}>
          <Icon icon="info" />
          <div className={css['info']}>
            <div>Reason for Visit</div>
            <div>{event.details.reason}</div>
          </div>
        </div>
      </div>
    </div>
  );

  const getAppointmentFooter = event => (
    <a 
      href={`../instructor/upcoming.html#/appointment/${event.details.id}`}
      className={css['link']}
      data-role='apptDetailsLink'>
      View details or cancel?
    </a>
  )

  const getBlockType = event => {
    switch (event.type) {
      case "ASSIGNMENT":
      case "APPOINTMENT":
      case "FLAG":
      case "REFERRAL":
        return event.type;
      case "GROUP_SESSION":
        return "GROUP SESSION";
      case "TO_DO":
        return "TO DO";
      case "CLASS":
        return "COURSE";
    }
  };

  const getBlockName = event => {
    switch (event.type) {
      case "ASSIGNMENT":
      case "GROUP_SESSION":
      case "TO_DO":
      case "FLAG":
      case "REFERRAL":
      case "CLASS":
        return event.name;
      case "APPOINTMENT":
        return event.details.instructor.name;
    }
  };

  const getAlertDetails = event => <InterventionDetails name={getBlockName(event)} type={event.type} {...event.details} />;

  const getDetails = event => {
    switch (event.type) {
      case "ASSIGNMENT": return getAssignmentDetails(event);
      case "APPOINTMENT":
      case "GROUP_SESSION":
        return getAppointmentDetails(event);
      case "TO_DO":
      case "FLAG":
      case "REFERRAL":
        return getAlertDetails(event);
      case "CLASS":
        return getCourseDetails(event);
    }
  };

  const renderFooter = event => {
    switch (event.type) {
      case "APPOINTMENT":
      case "GROUP_SESSION":
        if ( moment().isSameOrAfter(moment(`${event.date} ${event.startTime}`, "YYYY-MM-DD HH:mm")) ) return;
        return getAppointmentFooter(event);
    }
  };

  const cardFooter = (event) => {
    if (event.type !== 'TO_DO')
      return null
    if (event.details.closureReviewRequired && event.details.requestedResolutionDate)
      return <div className={css['pending-review']}>Pending Review</div>
    if ((event.details.studentCloseable || event.details.closureReviewRequired) && !event.details.requestedResolutionDate)
      return <DrawerOpenButton buttonText={'Mark As Complete'}
                               name={getBlockName(event)}
                               successToastMessage={successToastMessage}
                               type={getBlockType(event)}
                               requestHelp={requestHelp}
                               {...event.details} />
  }

  const renderDrawerCard = (event) =>
    <DrawerCard
        key={event.id}
        blockId={event.id}
        blockType={getBlockType(event)}
        blockIcon={getBlockIcon(event)}
        blockName={getBlockName(event)}
        footer={cardFooter(event)}>
      {getDetails(event)}
    </DrawerCard>


  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      overlayClassName={{
        base: css["overlay"],
        afterOpen: css["overlay-after-open"],
        beforeClose: css["overlay-before-close"]
      }}
      className={{
        base: css["modal"],
        afterOpen: css["modal-after-open"],
        beforeClose: css["modal-before-close"]
      }}
      contentLabel="Event Details"
      closeTimeoutMS={300}
    >
      <button
        ref={ref}
        onClick={onClose}
        aria-label="Close drawer" 
        className={css['close-button']}>
        <Icon aria-hidden={true} icon='close' />
      </button>
      <h2 className={css['header']}>{moment(date).format("dddd, MM-DD-YYYY")}</h2>
      {allDayEvents.length > 0 && (
        <div className={css['block-container']}>
          <h3>ALL DAY</h3>
          {allDayEvents.map(renderDrawerCard)}
        </div>
      )}
      {getCombinedEvents().map((block, i) => {
        if ( isArray(block) ) {
          return (
            <div key={`combined-${i}`} className={css['block-container']}>
              <h3>{formatTimeHeader(block[0])}</h3>
              {block.map(renderDrawerCard)}
            </div>
          )
        } else {
          return (
            <div key={`div-${block.id}`} className={css['block-container']}>
              <h3>{formatTimeHeader(block)}</h3>
              {renderDrawerCard(block)}
              {renderFooter(block)}
            </div>
          )
        }
      })}
      {(data.length === 0 && !isAllDayEvent) && <span className={css['empty-message']}>Nothing scheduled for this date.</span>}
      {(data.length === 0 && isAllDayEvent) && (
          <div className={css['empty-allday']}>
            <div className={css['empty-allday-message']}>
              <img src='../images/starfish/excited_starfish.png' aria-hidden='true' className={css['excited-starfish']}/>
              <span>No ALL DAY items for this date. Keep being awesome!</span>
            </div>
            <div className={css['empty-allday-note']}>Note: There may still be items on this day at a specified time. Please check your calendar.</div>
          </div>
      )}
    </Modal>
  );
});

CalendarDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  date: PropTypes.string.isRequired,
  successToastMessage: PropTypes.func.isRequired,
  requestHelp: RequestHelp.isRequired,
  isAllDayEvent: PropTypes.bool.isRequired
}

export default CalendarDrawer;
